import { HTMLBnumButton } from '../bnum-button';
import { ATTR_VARIATION } from './constants';
import { ButtonVariation } from './types';

/**
 * Décorateur de classe permettant de définir une variation par défaut pour un composant bouton.
 * Ce décorateur étend le constructeur de la classe cible (Pattern Proxy/Mixin) pour injecter
 * automatiquement la valeur de variation dans le dataset du composant lors de son instanciation.
 * @remarks
 * Ce décorateur utilise l'API standard ECMAScript (Stage 3). Il inclut une vérification
 * `instanceof` pour s'assurer que la classe décorée hérite bien de {@link HTMLBnumButton},
 * évitant ainsi des erreurs d'exécution sur des classes incompatibles.
 * @param variation - Le type de variation à appliquer (ex: 'primary', 'secondary').
 * Doit correspondre à une valeur valide de {@link ButtonVariation}.
 * @returns Une fonction décoratrice qui retourne la classe étendue avec la logique d'injection.
 * @example
 * ```typescript
 * import { HTMLBnumButton } from '../bnum-button';
 * import { Variation } from './decorators';
 * @Variation('primary')
 * export class PrimaryButton extends HTMLBnumButton {
 * // L'attribut ATTR_VARIATION sera défini à 'primary' dès la construction.
 * }
 * ```
 */
export function Variation(variation: ButtonVariation) {
  return function <T extends new (...args: any[]) => any>(
    originalClass: T,
    context: ClassDecoratorContext<T>,
  ) {
    if (context.kind !== 'class') return;

    class InnerClass extends (originalClass as any) {
      constructor(...args: any[]) {
        super(...args);

        if (this instanceof HTMLBnumButton) {
          const fromAttribute = false;
          this.data(ATTR_VARIATION, variation, fromAttribute);
        }
      }
    }

    return InnerClass as T;
  };
}
